import { useRef } from 'react';

/**
 * Returns the appropriate polling interval based on current job status and elapsed time
 * 
 * @param currentStatus - Current status of the job
 * @returns Polling interval in milliseconds or false to stop polling
 */
export function useSmartPolling(currentStatus?: string): number | false {
  // Mount time tracking for polling exponential backoff
  const mountTimeRef = useRef<number>(Date.now());
  
  // Log the status for debugging
  if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'finalized') {
    console.log(`Smart polling stopped: job status is ${currentStatus}`);
    return false; // Stop polling for completed/failed jobs
  }
  
  // If no status or not in a terminal state, continue polling with backoff
  const elapsedMinutes = (Date.now() - mountTimeRef.current) / (1000 * 60);
  
  // Responsive polling intervals for better user experience with scanning animation
  if (elapsedMinutes < 1) return 5000;       // First minute: every 5 seconds
  if (elapsedMinutes < 6) return 10000;      // 1-6 minutes: every 10 seconds  
  return 15000;                              // After 6 minutes: every 15 seconds
}

/**
 * Utility to determine if a job is in a completed state
 * @param status - Current job status
 * @returns boolean indicating if the job is completed
 */
export function isJobCompleted(status?: string): boolean {
  return status === 'completed' || status === 'failed' || status === 'finalized';
}
